import React, { createContext, useContext, useEffect, useState } from 'react';
import { app } from '@microsoft/teams-js';

interface TeamAppContextType {
  isInTeams: boolean;
  isLoading: boolean;
  /** Teams user (UPN) used as the MSAL login hint for silent sign-in; null outside Teams. */
  teamsLoginHint: string | null;
}

const TeamAppContext = createContext<TeamAppContextType>({
  isInTeams: false,
  isLoading: true,
  teamsLoginHint: null,
});

export const useTeamApp = () => useContext(TeamAppContext);

export const TeamAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInTeams, setIsInTeams] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [teamsLoginHint, setTeamsLoginHint] = useState<string | null>(null);

  useEffect(() => {
    const checkTeamsEnvironment = async () => {
      try {
        // Initialize Teams SDK
        await app.initialize();
        console.log(
          '%c[Teams]%c App is running in Microsoft Teams',
          'background: #0078d4; color: white; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
          'color: #0078d4; font-weight: bold; margin-left: 5px;'
        );

        // Check if we're running in Teams
        const context = await app.getContext();
        const user = context?.user;
        setTeamsLoginHint(user?.userPrincipalName ?? user?.loginHint ?? null);
        setIsInTeams(true);
      } catch (error) {
        // If initialization fails, we're not in Teams
        console.warn('Teams SDK initialization failed:', error);
        setIsInTeams(false);
        setTeamsLoginHint(null);
        console.log(
            '%c[Teams]%c App is not running in Microsoft Teams',
            'background: #ff0000; color: white; padding: 2px 5px; border-radius: 3px; font-weight: bold;',
            'color: #ff0000; font-weight: bold; margin-left: 5px;'
        );
      } finally {
        setIsLoading(false);
      }
    };

    checkTeamsEnvironment();
  }, []);

  return (
    <TeamAppContext.Provider value={{ isInTeams, isLoading, teamsLoginHint }}>
      {children}
    </TeamAppContext.Provider>
  );
};
